import type { BackendTypes } from '@tf/api';
import { Box, createStyles, TFCard, TFText } from '@tf/ui';
import { fmt, S } from '@tf/utils';
import type React from 'react';
import type { PropsWithChildren } from 'react';
import { AssignedPaymentManagersWidget } from '@/components/payments';
import { TFBadge, TFBadgeColors } from '@/components/shared';
import type { PaymentRisk, PaymentStatus } from '@/core/types/payments';

const useStyles = createStyles(({ colors, radius }) => {
	const borderColor = colors.gray[3];

	return {
		wrapper: {
			paddingTop: '0.5rem',
			paddingBottom: '0.5rem',
			display: 'flex',
			borderRadius: radius.sm,
			minHeight: '4.7rem',

			'& > :not(:first-of-type)': {
				borderLeft: `1px solid ${borderColor}`,
			},
		},
		item: {
			paddingLeft: '0.75rem',
			paddingRight: '0.75rem',
			minWidth: '132px',
		},
		itemTitle: {
			color: colors.gray[5],
			fontWeight: 600,
			fontSize: '0.75rem',
			letterSpacing: '.5px',
		},
	};
});

const Item: React.FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => {
	const { classes } = useStyles();

	return (
		<Box className={classes.item}>
			<TFText pb="0.15rem" className={classes.itemTitle}>
				{label}
			</TFText>
			{children}
		</Box>
	);
};

export const paymentStatusBadgeColors: Record<PaymentStatus, string> = {
	UNKNOWN: TFBadgeColors.DIMMED,
	PENDING_CHECK: TFBadgeColors.DIMMED,
	PENDING_REVIEW: TFBadgeColors.BLUE,
	PENDING_DETAILS: TFBadgeColors.BLUE,
	APPROVED: TFBadgeColors.GREEN,
	REJECTED: TFBadgeColors.RED,
	APPROVED_ERROR: TFBadgeColors.DIMMED,
	REJECTED_ERROR: TFBadgeColors.DIMMED,
	ORDER_COMPLETED: TFBadgeColors.GREEN,
	ORDER_REJECTED: TFBadgeColors.RED,
	FAILED: TFBadgeColors.RED,
};

export const paymentRiskBadgeColors: Record<PaymentRisk, string> = {
	UNKNOWN: TFBadgeColors.DIMMED,
	NOT_ASSESSED: TFBadgeColors.DIMMED,
	LOW: TFBadgeColors.GREEN,
	MEDIUM: TFBadgeColors.ORANGE,
	HIGH: TFBadgeColors.RED,
};

interface Props {
	orderId: string;
	paymentId: string;
	status?: PaymentStatus;
	riskLevel?: PaymentRisk;
	assignedManagers: string[];
	listUsers: BackendTypes.UserInfo[];
	createdAt: string;
	updatedAt: string;
}

export const PaymentToolbar: React.FC<Props> = ({
	orderId,
	paymentId,
	status = 'UNKNOWN',
	riskLevel = 'UNKNOWN',
	assignedManagers,
	listUsers,
	createdAt,
	updatedAt,
}) => {
	const { classes } = useStyles();

	return (
		<TFCard>
			<Box className={classes.wrapper}>
				<Item label="Order ID">
					<TFText c="111827" fz={14}>
						{orderId}
					</TFText>
				</Item>
				<Item label="Payment status">
					<TFBadge text={S.prettify(status)} color={paymentStatusBadgeColors[status] as TFBadgeColors} disabled />
				</Item>
				<Item label="Risk">
					<TFBadge text={S.prettify(riskLevel)} color={paymentRiskBadgeColors[riskLevel] as TFBadgeColors} disabled />
				</Item>
				<Item label="Assignee">
					<AssignedPaymentManagersWidget paymentId={paymentId} selectedIds={assignedManagers} managers={listUsers} />
				</Item>
				<Item label="Instruction received">
					<TFText c="111827" fz={14}>
						{fmt.toDate(createdAt, { preset: 'full_date_with_time' })}
					</TFText>
				</Item>
				<Item label="Last status changes">
					<TFText c="111827" fz={14}>
						{fmt.toDate(updatedAt, { preset: 'full_date_with_time' })}
					</TFText>
				</Item>
			</Box>
		</TFCard>
	);
};
